/**
 * The level's item choices (spec 02 §7.3, §7.4, §9.3), ported from level-setup.s: the food (L_2BB0),
 * the door rule ($2BDB), the power-up (L_2BFC) with the C64's own PRNG (D_E9EA) where the choice must
 * match the original - the designated power-up of each level - and the game's PCG32 elsewhere.
 */

import {
  ANY_POWERUP_MASK, AFTER_WHITE_DOOR, BALL_OR_BELL, BONUS_BAND_SEED, CHOICE_MASK, CIA_TIMER_B_LOW, DESIGNATED_FROM,
  DOOR_STEP, FOOD_MAX, FOOD_ROLL_MASK, NEW_GAME_BASE, NEW_GAME_SPAN, WHITE_DOOR_AT,
} from './tuning';
import { RED_DOOR, WHITE_DOOR } from './powerups';
import type { Pcg32 } from '../rng';
import type { CarryState } from '../types';

/** The PRNG's state: zero page $26 (RESHO) and $27. */
export interface C64Rng {
  lo: number;
  hi: number;
}

/**
 * D_E9EA, instruction for instruction: `lda $26 / asl / rol $27 / rol $26 / lda $27 / eor $26 /
 * adc $27 / eor $DC06 / sta $26`. The carry into the `adc` is the old bit 7 of $26.
 */
export function c64Random(s: C64Rng, cia = CIA_TIMER_B_LOW): number {
  const c1 = (s.lo >> 7) & 1;
  const c2 = (s.hi >> 7) & 1;
  s.hi = ((s.hi << 1) | c1) & 0xff;
  const c3 = (s.lo >> 7) & 1;
  s.lo = ((s.lo << 1) | c2) & 0xff;
  const a = (((s.hi ^ s.lo) + s.hi + c3) & 0xff) ^ cia;
  s.lo = a & 0xff;
  return s.lo;
}

/** L_2BFC: the level's designated power-up - the PRNG seeded with the level index in both bytes, + 1, & 31. */
export function designatedPowerup(levelIndex: number): number {
  const s: C64Rng = { lo: levelIndex & 0xff, hi: levelIndex & 0xff };
  return (c64Random(s) + 1) & ANY_POWERUP_MASK;
}

/** L_2BFC..L_2C2D: rand & 15 - 0 the purple crystal ball or bell, 7..15 designated, 1..6 any of 0..31. */
export function choosePowerup(levelIndex: number, rng: Pcg32): number {
  const roll = rng.int(256) & CHOICE_MASK;
  if (roll === 0) return BALL_OR_BELL | (rng.int(256) & 1);
  if (roll >= DESIGNATED_FROM) return designatedPowerup(levelIndex);
  // The code allows every item here: geon's "never shoe, white or red staff" is not in L_2C2D.
  return rng.int(256) & ANY_POWERUP_MASK;
}

/**
 * L_2BB0 and D_2BBD: the food is the base ($58) + rand & 3, 8-bit, capped at 46; then $58 = 0 (so a level
 * left without a clear hands the next one 0). A new game rolls the base first (game-loop.s $093F).
 */
export function chooseFood(carry: CarryState, rng: Pcg32): number {
  if (carry.foodBase < 0) carry.foodBase = NEW_GAME_BASE + rng.int(NEW_GAME_SPAN);
  const food = (carry.foodBase + (rng.int(256) & FOOD_ROLL_MASK)) & 0xff;
  carry.foodBase = 0;
  return food > FOOD_MAX ? FOOD_MAX : food;
}

/**
 * $2BDB: while a starting player has never died (D_5B7F), a door replaces the power-up when the level
 * index passes the counter (D_5B3F, from 18), which then gains 10; reaching 58 makes it the white door
 * and the counter 78. Returns the door (RED_DOOR / WHITE_DOOR) or -1.
 */
export function doorFor(carry: CarryState, levelIndex: number): number {
  if (!carry.unharmed[0] && !carry.unharmed[1]) return -1;
  if (carry.doorAt >= levelIndex) return -1;
  carry.doorAt += DOOR_STEP;
  if (carry.doorAt !== WHITE_DOOR_AT) return RED_DOOR;
  carry.doorAt = AFTER_WHITE_DOOR;
  return WHITE_DOOR;
}

/**
 * $2F91..$2FA4: the bonus stage fills 2-row bands from map row 1; their number is 42 ^ levelIndex,
 * brought below 11 by tens. 0 (level 43 only) makes the C64 scan on past the map; see `bonusCells`.
 */
export function bonusBands(levelIndex: number): number {
  let a = BONUS_BAND_SEED ^ (levelIndex & 0xff);
  while (a >= 11) a -= 10;
  return a;
}
